<script lang="ts">
  import { triggerOpportunityDataRefresh } from "../lib/syncDashboardRefresh";

  let syncing = false;
  let syncResult: any = null;
  let syncError = "";
  let syncDirection = "";

  const baseUrl = `${process.env.SVELTE_APP_REMOTE_URL}/api`;

  function getErrorMessage(response: any): string {
    if (!response) return "Unknown error";
    if (response.ErrorList?.length) {
      return response.ErrorList.map((e: any) => e.Message).join("; ");
    }
    return response.Message || "Unknown error";
  }

  const syncFromAWS = async () => {
    syncing = true;
    syncResult = null;
    syncError = "";
    syncDirection = "from";

    try {
      const res = await fetch(`${baseUrl}/sync/from-aws`, {
        method: "GET",
      });
      const data = await res.json();
      syncResult = { success: res.ok, ...data };
      if (res.ok && data?.success !== false) {
        triggerOpportunityDataRefresh();
      }
    } catch (err: any) {
      syncError = `Failed to sync from AWS: ${err?.message || err}`;
    } finally {
      syncing = false;
    }
  };

  const syncToAWS = async () => {
    syncing = true;
    syncResult = null;
    syncError = "";
    syncDirection = "to";
    try {
      const res = await fetch(`${baseUrl}/sync/to-aws`, {
        method: "GET",
      });
      const data = await res.json();
      syncResult = data;
      if (res.ok && data?.success !== false) {
        triggerOpportunityDataRefresh();
      }
    } catch (err) {
      syncError = "Failed to sync with AWS.";
    } finally {
      syncing = false;
    }
  };

  function dismissResult() {
    syncResult = null;
    syncError = "";
  }
</script>

<div class="flex gap-4 mb-6">
  <button
    on:click={syncFromAWS}
    class="px-4 py-2 text-sm font-semibold bg-primary-600 text-white rounded-lg shadow hover:bg-primary-700 transition disabled:opacity-50"
    disabled={syncing}
  >
    {syncing ? "Syncing..." : "⬇ Sync from AWS"}
  </button>

  <button
    on:click={syncToAWS}
    class="btn-chip px-4 py-2 text-sm font-semibold rounded-lg transition disabled:opacity-50"
    disabled={syncing}
  >
    {syncing ? "Syncing..." : "⬆ Sync to AWS"}
  </button>
</div>

<style>
  .btn-chip {
    background: #303650;
    border: 1px solid rgba(226, 232, 240, 0.22);
    color: #e8ecf6;
  }

  .btn-chip:hover:not(:disabled) {
    background: #3b415c;
    color: #ffffff;
  }
</style>

{#if syncError}
  <div class="text-sm text-red-800 bg-red-50 border border-red-200 p-3 rounded shadow">
    {syncError}
    <button on:click={dismissResult} class="ml-2 text-red-500 hover:text-red-700 font-bold">&times;</button>
  </div>
{/if}

{#if syncResult}
  <div class="text-sm bg-white border border-gray-200 rounded-lg shadow overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 {syncResult.success ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'}">
      <div class="flex items-center gap-2">
        <span class="font-semibold {syncResult.success ? 'text-green-800' : 'text-red-800'}">
          {syncResult.success ? '✓' : '✗'} Sync {syncDirection} AWS {syncResult.success ? 'completed' : 'completed with errors'}
        </span>
      </div>
      <div class="flex items-center gap-3 text-xs">
        {#if syncResult.synced?.length}
          <span class="bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">{syncResult.synced.length} synced</span>
        {/if}
        {#if syncResult.failed?.length}
          <span class="bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-medium">{syncResult.failed.length} failed</span>
        {/if}
        {#if syncResult.skipped?.length}
          <span class="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full font-medium">{syncResult.skipped.length} skipped</span>
        {/if}
        <button on:click={dismissResult} class="text-gray-400 hover:text-gray-600 font-bold text-lg leading-none">&times;</button>
      </div>
    </div>

    <!-- Scrollable content -->
    <div class="max-h-64 overflow-y-auto">
      <!-- Failed items -->
      {#if syncResult.failed?.length}
        <div class="px-4 py-2 bg-red-50">
          <div class="font-medium text-red-700 mb-1">Failed ({syncResult.failed.length})</div>
          {#each syncResult.failed as id}
            <div class="mb-2 p-2 bg-white border border-red-200 rounded text-xs">
              <div class="font-medium text-gray-700">Item #{id}</div>
              {#if syncResult.responses?.[id]}
                <div class="text-red-600 mt-1">
                  {#if syncResult.responses[id].Reason}
                    <span class="font-medium">{syncResult.responses[id].Reason}</span>
                  {/if}
                  {#if syncResult.responses[id].ErrorList}
                    {#each syncResult.responses[id].ErrorList as error}
                      <div class="mt-0.5 text-red-500">
                        <span class="font-medium">{error.FieldName}:</span> {error.Message}
                      </div>
                    {/each}
                  {/if}
                </div>
              {/if}
            </div>
          {/each}
        </div>
      {/if}

      <!-- Synced items -->
      {#if syncResult.synced?.length}
        <div class="px-4 py-2">
          <div class="font-medium text-green-700 mb-1">Synced ({syncResult.synced.length})</div>
          <div class="grid grid-cols-2 gap-1">
            {#each syncResult.synced as id}
              <div class="text-xs text-gray-600 p-1 bg-green-50 rounded flex justify-between">
                <span>#{id}</span>
                {#if syncResult.responses?.[id]?.Id}
                  <span class="text-gray-400">{syncResult.responses[id].Id}</span>
                {/if}
              </div>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Skipped items -->
      {#if syncResult.skipped?.length}
        <div class="px-4 py-2 bg-yellow-50">
          <div class="font-medium text-yellow-700 mb-1">Skipped ({syncResult.skipped.length})</div>
          <div class="flex flex-wrap gap-1">
            {#each syncResult.skipped as id}
              <span class="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">#{id}</span>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>
{/if}
